import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Spinner } from 'react-bootstrap';
import { ProductContext } from '../../context/ProductContext';

const moveProduct = (products, fromIndex, toIndex) => {
  if (toIndex < 0 || toIndex >= products.length || fromIndex === toIndex) {
    return products;
  }

  const nextProducts = [...products];
  const [movedProduct] = nextProducts.splice(fromIndex, 1);
  nextProducts.splice(toIndex, 0, movedProduct);
  return nextProducts;
};

const AdminOrderPage = () => {
  const { products, reorderProducts } = useContext(ProductContext);
  const [orderedProducts, setOrderedProducts] = useState([]);
  const [draggedProductId, setDraggedProductId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setOrderedProducts(products);
  }, [products]);

  const hasChanges = useMemo(
    () => orderedProducts.some((product, index) => product.id !== products[index]?.id),
    [orderedProducts, products]
  );

  const handleDrop = (targetIndex) => {
    const sourceIndex = orderedProducts.findIndex((product) => product.id === draggedProductId);
    if (sourceIndex === -1) {
      return;
    }

    setOrderedProducts((currentProducts) => moveProduct(currentProducts, sourceIndex, targetIndex));
    setDraggedProductId(null);
    setSaved(false);
    setError('');
  };

  const handleMove = (index, direction) => {
    setOrderedProducts((currentProducts) => moveProduct(currentProducts, index, index + direction));
    setSaved(false);
    setError('');
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaved(false);
    setError('');

    try {
      await reorderProducts(orderedProducts);
      setSaved(true);
    } catch (saveError) {
      setError(saveError.message || 'Failed to save product order.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center gap-3 mb-3 text-start">
        <div className="text-start">
          <h2 className="mb-1">Product Order</h2>
          <p className="admin-product-order-description mb-0">Drag products into the order customers should see.</p>
        </div>
        <Button variant="primary" onClick={handleSave} disabled={!hasChanges || isSaving}>
          {isSaving ? <><Spinner animation="border" size="sm" className="me-2" />Saving...</> : 'Save Order'}
        </Button>
      </div>

      {saved && <Alert variant="success">Product order saved.</Alert>}
      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Card.Body>
          {orderedProducts.length === 0 ? (
            <p className="admin-empty-message text-center mb-0">No products to order.</p>
          ) : (
            <div className="admin-product-order-list" aria-label="Product order list">
              {orderedProducts.map((product, index) => (
                <div
                  className={`admin-product-order-item${draggedProductId === product.id ? ' is-dragging' : ''}`}
                  draggable
                  key={product.id}
                  onDragStart={(event) => {
                    event.dataTransfer.effectAllowed = 'move';
                    event.dataTransfer.setData('text/plain', String(product.id));
                    setDraggedProductId(product.id);
                  }}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => handleDrop(index)}
                  onDragEnd={() => setDraggedProductId(null)}
                >
                  <div className="admin-product-order-position">{index + 1}</div>
                  <div className="admin-product-order-handle" aria-hidden="true">☷</div>
                  <div className="flex-grow-1 text-start">
                    <div className="fw-semibold">{product.name}</div>
                    <div className="small text-muted">
                      {product.category || 'Uncategorized'} · ${Number(product.price || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      aria-label={`Move ${product.name} up`}
                      onClick={() => handleMove(index, -1)}
                      disabled={index === 0}
                    >
                      ↑
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      aria-label={`Move ${product.name} down`}
                      onClick={() => handleMove(index, 1)}
                      disabled={index === orderedProducts.length - 1}
                    >
                      ↓
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default AdminOrderPage;
